import calcCharacteristicValues from '../helpers/calcCharacteristicValues'
import calcAbilities from '../helpers/calcAbilities'
import calcGeneralDetails from '../helpers/calcGeneralDetails'
import calcSkillAdjTotals from '../helpers/calcSkillAdjTotals'
import getSkillAdjReasons from '../helpers/getSkillAdjReasons'
import calcCurrentAbility from '../helpers/calcCurrentAbility'
import calcSkillValues from '../helpers/calcSkillValues'
import buildDamageMod from '../helpers/buildDamageMod'
import getAdjReasons from '../helpers/getAdjReasons'
import calcAdjsTotal from '../helpers/calcAdjsTotal'
import syncSkillCharacterSkillAdjsFromDetailEntries from '../helpers/syncSkillCharacterSkillAdjsFromDetailEntries'

import store from '../store/index'
//-------------------------------------------------
// process calc totals rebuild reason applied adjs
//-------------------------------------------------
export default function processCalcTotalsRebuildReasonAppliedAdjs({
  payload = {},
  character = null,
  add_only = false,
}) {
  /*
  SUMMARY:
  --------
  This function is called from:
    - processCalcTotalsRebuildReasonAppliedAdjsHarness (when adding runes/runecasting skills) - add_only = true
    - adj management view for applied adjs (where existing adjs need to be cleared first) - add_only = false
    - NB: skill_adjs holds both applied & optional adjs altho here only applied adjs are being updated
  */
  //-----------
  // get parms
  //-----------
  const category = payload.category ? payload.category : ''
  const type = payload.type ? payload.type : ''
  const adjs = payload.data ? payload.data : {}

  if (!character) {
    return null
  }
  //----------------------------------------------------------
  // park optional adjs so only applied adjs are processed here
  //----------------------------------------------------------
  // NB: this separation of applied vs optional is only meaningful
  // for skill adjs. For ability/characteristic we treat all adjs
  // together to avoid creating duplicate entries for the same adj.
  let parkedOptionalAdjs = []
  if (category === 'skill') {
    const adjsKey = `${category}_adjs`
    const allCategoryAdjs = character[adjsKey] || []
    parkedOptionalAdjs = allCategoryAdjs.filter((adj) => adj.applied === false)
    const appliedOnlyAdjs = allCategoryAdjs.filter((adj) => adj.applied === true)
    character[adjsKey] = appliedOnlyAdjs
  }
  //-------------------
  // get config values
  //-------------------
  const config = store.getters['combat/getConfig']
  const max_skill = config ? config.max_skill : 100
  const min_ca = config ? config.min_ca : 1
  const max_ca = config ? config.max_ca : 5
  const min_cr = config ? config.min_cr : 1
  const max_cr = config ? config.max_cr : 5
  /*
  add_only:
  ---------
  - true = called from adding rune/runecasting skill - so no need to clear existing adjs
  - false = called from adj management view - so need to clear existing adjs first
  */
  //------------------------------------------------------
  // update adjs (already filtered for applied adjs only)
  //------------------------------------------------------
  if (!add_only) {
    character = _updateAdjs({ adjs: adjs, category: category, type: type, character: character })
  }
  //===================================================================================================
  // A. where category = SKILLS [NB: skills adjs at individual skill level / others at category level]
  //===================================================================================================
  if (category == 'skill') {
    let matching_skills = []
    //-----------------------------------------------
    // A. runecasting per-rune skills (ensure integrated rune family for skill)
    //    e.g. type = 'Air Rune', 'Disorder Rune'. For the base
    //    Runecasting skill itself (type === 'Runecasting'), fall
    //    back to default name matching so that global integrated
    //    Runecasting adjs apply to all Runecasting skills.
    //-----------------------------------------------
    if (type.includes('Rune') && type !== 'Runecasting') {
      matching_skills = character.skills.filter((s) => {
        const matchesRuneName = s.display_name === type
        if (!matchesRuneName) {
          return false
        }
        //-------------------------------------------------
        // only match where associated rune family exists
        // in an integrated state on the character
        //-------------------------------------------------
        const integrated_rune =
          character.runes && character.runes.length > 0
            ? character.runes.find((r) => r.name === s.display_name && r.integrated === true)
            : null
        return !!integrated_rune
      })
      //--------------------------------------------------------------------------------------------------------------------
      // B. default: match by name (covers both 'normal' skills [should only get only] & runecasting with all_in_type=true)
      //--------------------------------------------------------------------------------------------------------------------
    } else {
      matching_skills = character.skills.filter((s) => s.name === type)
    }
    //---------------------
    // got matching skills
    //---------------------
    if (matching_skills && matching_skills.length > 0) {
      //--------------------
      // loop matching skills
      //---------------------
      matching_skills.forEach((skill) => {
        //--------------------------
        // update skill adjs totals
        //--------------------------
        skill = _updateSkillAdjTots({ category, type, skill, character })
        //---------------------------
        // update skill adjs reasons
        //---------------------------
        skill = _updateSkillAdjReasons({ category, type, skill, character })
        //--------------------------------------------------------
        // recalc current skill (plus other skill-related values)
        //--------------------------------------------------------
        skill = _recalcSkillValues({ type, skill, character, max_skill })
        //-------------------------------------------
        // trigger Vue reactivity with new array ref
        // -------------------------------------------
        let idx = character.skills.findIndex((s) => s.id === skill.id)
        if (idx !== -1) {
          character.skills[idx] = skill
        }
      })
    }

    syncSkillCharacterSkillAdjsFromDetailEntries(character)
    //=====================================================================
    // B. where NON-SKILL category
    //=====================================================================
  } else {
    //------------------------------
    // update non-skill adjs totals
    _updateAdjsTot({ category: category, type: type, character: character })
    //------------------------------
    // update non-skill adjs reasons
    _updateAdjsReasons({ category: category, type: type, character: character })
    //=====================================================================
    // B1. where category = ABILITY
    //=====================================================================
    if (category == 'ability') {
      character = _recalcCurrentAbility({
        type: type,
        character: character,
        min_ca,
        max_ca,
        min_cr,
        max_cr,
      })
      //=====================================================================
      // B2. where category = CHARACTERISTIC
      //=====================================================================
    } else if (category == 'characteristic') {
      character = calcCharacteristicValues({ pfx: type, character: character })
      character = calcAbilities({ pfx: type, character: character })
      character = calcGeneralDetails({ character })
    }
  }
  //------------------------------------------
  // re-attach parked optional adjs (if any)
  //------------------------------------------
  if (category === 'skill' && parkedOptionalAdjs.length > 0) {
    const adjsKey = `${category}_adjs`
    const appliedNow = character[adjsKey] || []
    const combined = [...appliedNow, ...parkedOptionalAdjs]

    // Deduplicate combined skill_adjs to prevent duplicate
    // entries for the same adjustment (e.g. identical rune
    // optional adjs). Use a composite key that covers the
    // identifying fields but preserves legitimately distinct
    // adjustments (different item, value, etc.).
    const seen = new Set()
    const deduped = []
    combined.forEach((adj) => {
      const key = [
        adj.applied === true,
        adj.name || '',
        adj.adj_reason || '',
        adj.adj_value ?? '',
        adj.character_item ?? '',
      ].join('|')
      if (!seen.has(key)) {
        seen.add(key)
        deduped.push(adj)
      }
    })

    character[adjsKey] = deduped
  }

  if (category === 'skill') {
    syncSkillCharacterSkillAdjsFromDetailEntries(character)
  }
  //--------------------------
  // return mutated character
  //--------------------------
  return character
  //=================================================================================
  // INTERNAL FUNCTION: update adjs
  // -------------------------------
  function _updateAdjs({ adjs, category, type, character }) {
    //-------------------------------------------------------------------
    // remove all adjs for this specific type (optionals already parked)
    //-------------------------------------------------------------------
    // NB: but this could leave runecasting adjs for this skill
    // So need to check adjs being added below to ensure no duplicates
    //-------------------------------------------------------------------
    character[`${category}_adjs`] = character[`${category}_adjs`].filter((adj) => adj.name !== type)
    //---------------------------------------------------------
    // add updated adjs impacted category (i.e. for this type) [could be empty if no adjs/adjs removed]
    //---------------------------------------------------------
    if (adjs && adjs.length > 0) {
      //--------------------------
      // loop adjs for this skill
      //--------------------------
      adjs.forEach((a) => {
        const isRemovalMarker =
          Number(a?.adj_value ?? 0) === 0 && `${a?.adj_reason ?? ''}`.trim() === ''

        if (isRemovalMarker) {
          return
        }
        //----------------------------------------------------
        // check exists (prevent dupes) e.g. runecasting adjs
        //----------------------------------------------------
        const exists = character[`${category}_adjs`].some(
          (existing_adj) =>
            existing_adj.name === a.name && existing_adj.adj_reason === a.adj_reason,
        )
        //-----------------------
        // already exists - skip
        //-----------------------
        if (exists) {
          //----------------------
          // does not exist - add
          //----------------------
        } else {
          character[`${category}_adjs`].push(window.$deepCopy(a))
        }
      })
    }
    //--------------------------------------
    // return updated character for clarity
    //--------------------------------------
    return character
  }
  //=================================================================================
  // INTERNAL FUNCTION: update adjs total
  // -------------------------------------
  function _updateAdjsTot({ category, type, character }) {
    const total = calcAdjsTotal({
      adjs: character[`${category}_adjs`],
      type: type,
    })
    character[`${type}_adjs_tot`] = total
    //--------------------------------------
    // return updated character for clarity
    //--------------------------------------
    return character
  }
  //=================================================================================
  // INTERNAL FUNCTION: update adjs reasons
  // ---------------------------------------
  function _updateAdjsReasons({ category, type, character }) {
    getAdjReasons({
      character: character,
      category: category,
      type: type,
    })
    //--------------------------
    // return updated character
    //--------------------------
    return character
  }
  //=================================================================================
  // INTERNAL FUNCTION: update skill adjs totals
  // --------------------------------------------
  function _updateSkillAdjTots({ category, type, skill, character }) {
    const return_totals = calcSkillAdjTotals({
      category_adjs: character[`${category}_adjs`],
      skill: skill,
      type: type,
    })
    //-------------------------------------------------------------
    // check if adj props exist - if not, create new object with them
    //-------------------------------------------------------------
    if (!('adjs_tot' in skill)) {
      return {
        ...skill,
        adjs_tot: return_totals.adjs_tot,
        adjs_tot_character_skill: return_totals.adjs_tot_character_skill,
        adjs_tot_characteristic_skill: return_totals.adjs_tot_characteristic_skill,
        adjs_tot_integrated_rune_skill: return_totals.adjs_tot_integrated_rune_skill,
      }
    }
    //------------------------------
    // update skill with adj totals
    //------------------------------
    skill.adjs_tot = return_totals.adjs_tot
    skill.adjs_tot_character_skill = return_totals.adjs_tot_character_skill
    skill.adjs_tot_characteristic_skill = return_totals.adjs_tot_characteristic_skill
    skill.adjs_tot_integrated_rune_skill = return_totals.adjs_tot_integrated_rune_skill
    //----------------------
    // return updated skill
    //----------------------
    return skill
  }
  //=================================================================================
  // INTERNAL FUNCTION: update skill adjs reasons
  // ---------------------------------------------
  function _updateSkillAdjReasons({ category, type, skill, character }) {
    const return_reasons = getSkillAdjReasons({
      adjs: character[`${category}_adjs`],
      skill: skill,
      type: type,
    })
    //----------------------------------------------------------------
    // check if adj props exist - if not, create new object with them
    //----------------------------------------------------------------
    if (!('adjs_reasons_character_skill' in skill)) {
      return {
        ...skill,
        adjs_reasons_character_skill: return_reasons.adjs_reasons_character_skill,
        adjs_reasons_characteristic_skill: return_reasons.adjs_reasons_characteristic_skill,
        adjs_reasons_integrated_rune_skill: return_reasons.adjs_reasons_integrated_rune_skill,
      }
    }
    //-------------------------------
    // update skill with adj reasons
    //-------------------------------
    skill.adjs_reasons_character_skill = return_reasons.adjs_reasons_character_skill
    skill.adjs_reasons_characteristic_skill = return_reasons.adjs_reasons_characteristic_skill
    skill.adjs_reasons_integrated_rune_skill = return_reasons.adjs_reasons_integrated_rune_skill
    //----------------------
    // return updated skill
    //----------------------
    return skill
  }
  //=================================================================================
  // INTERNAL FUNCTION: recalculate skill values
  // --------------------------------------------
  function _recalcSkillValues({ type, character, skill, max_skill }) {
    //-------------------
    // calc skill values
    //-------------------
    calcSkillValues({
      skill,
      character,
      max_skill,
    })
    //----------------------
    // return updated skill
    //----------------------
    return skill
  }
  //=================================================================================
  // INTERNAL FUNCTION: recalculate current ability
  // -----------------------------------------------
  function _recalcCurrentAbility({ type, character, min_ca, max_ca, min_cr, max_cr }) {
    //---------------------------
    // build dam mod melee (cur)
    //---------------------------
    if (type == 'damage_mod_melee') {
      character.damage_mod_melee_cur = buildDamageMod({
        base: character.damage_mod_melee_base.toString(),
        adjs_tot: character.damage_mod_melee_adjs_tot
          ? character.damage_mod_melee_adjs_tot.toString()
          : '',
      })
      //---------------------------
      // build dam mod ranged (cur)
      //---------------------------
    } else if (type == 'damage_mod_ranged') {
      character.damage_mod_ranged_cur = buildDamageMod({
        base: character.damage_mod_ranged_base.toString(),
        adjs_tot: character.damage_mod_ranged_adjs_tot
          ? character.damage_mod_ranged_adjs_tot.toString()
          : '',
      })
      //-----------------------------
      // recalc ca, cr, sr, move, fly
      //------------------------------
    } else {
      //------------------
      // set initial parm
      //------------------
      let parm = {
        base: character[`${type}_base`],
        adjs_tot: character[`${type}_adjs_tot`],
      }
      //------------------------
      // set max where ca or cr
      //------------------------
      if (type == 'ca') {
        parm.max = max_ca
        parm.min = min_ca
      } else if (type == 'cr') {
        parm.max = max_cr
        parm.min = min_cr
      }
      //-------------------------
      // go calc current ability
      //-------------------------
      character[`${type}_cur`] = calcCurrentAbility(parm)
    }
    //--------------------------------------
    // return updated character for clarity
    //--------------------------------------
    return character
  }
}
